import React from 'react';
import { useTranslation } from 'react-i18next';

import LoadingButton from '@mui/lab/LoadingButton';
import Button from '@mui/material/Button';

import SSOLink from '~/components/SSOLink';
import {
  productTypes,
  useCtbProductInfo,
  useCtbPurchaseStatusQuery,
} from '~/hooks/useCtbProductInfo';

import { ActionType } from './Header';

interface ActionConfig {
  type?: ActionType;
  visible?: boolean;
  variant?: 'outlined' | 'contained';
  queryParams?: Record<string, string>;
  textKey?: string;
}

interface ActionButtonProps {
  config: ActionConfig;
  status: string;
  hostingPlanId?: string;
  onOpenServerEmail: () => void;
  onUpdateHostingId: (id?: string) => void;
  buttonProps: Record<string, any>;
  loading?: boolean;
  isServerOnline?: boolean;
  canAccessEmail?: boolean;
  isHostingPlanReady: boolean;
  isPackageExpired: boolean;
  hostingPlanType: string | null;
  domainsUsed: number | string | null;
}

const ActionButton: React.FC<ActionButtonProps> = ({
  config,
  status,
  hostingPlanId = undefined,
  onOpenServerEmail,
  onUpdateHostingId,
  buttonProps,
  loading = false,
  isServerOnline = false,
  canAccessEmail = false,
  isHostingPlanReady,
  isPackageExpired,
  hostingPlanType,
  domainsUsed,
}) => {
  const { type, variant, queryParams, textKey, visible } = config;
  const { t } = useTranslation('hosting', { keyPrefix: 'hostingNova' });
  const isDisabled = status !== 'active';
  const isActionDisabled = isDisabled || isPackageExpired;
  //CTB upgrade status
  const productInfo = useCtbProductInfo(
    productTypes.HOSTING_UPGRADE,
    hostingPlanId,
  );
  const { hasPurchasedRecently } = useCtbPurchaseStatusQuery(
    productInfo.purchaseQueryKey,
  );
  const isSelfManageButtonDisabled =
    hasPurchasedRecently || isActionDisabled || !isHostingPlanReady;

  const isSelfManagedVPS = hostingPlanType === 'vps';
  // Single early return check for visibility and status
  if (!visible || (!isSelfManagedVPS && isDisabled)) return null;

  const handleClick = () => {
    if (type === 'website') onUpdateHostingId?.(hostingPlanId);
    if (type === 'cpanelEmail') onOpenServerEmail?.();
  };
  const handleManage = () => {
    onUpdateHostingId?.(hostingPlanId);
  };
  // Apply textTransform: 'none' for cpanel buttons
  const cpanelButtonProps =
    type === 'cpanel' || type === 'resellerCpanel'
      ? { ...buttonProps, textTransform: 'none' as const }
      : buttonProps;

  const isCpanelEmailDisabled =
    !isServerOnline ||
    isDisabled ||
    !canAccessEmail ||
    Number(domainsUsed) === 0;

  switch (type) {
    case 'website':
      return (
        <LoadingButton
          variant={variant}
          disabled={isDisabled}
          onClick={handleClick}
          data-testid="btn-website"
          loading={loading}
          {...buttonProps}
        >
          {t(textKey ?? '')}
        </LoadingButton>
      );
    case 'whm':
      return (
        <SSOLink
          variant={variant}
          type="vps"
          queryParams={queryParams}
          hostingId={hostingPlanId}
          data-testid="btn-whm"
          {...buttonProps}
        >
          {t(textKey ?? '')}
        </SSOLink>
      );
    case 'adminPanel':
      return (
        <SSOLink
          variant={variant}
          type="plesk"
          hostingId={hostingPlanId}
          data-testid="btn-admin-panel"
          disabled={isDisabled}
          {...buttonProps}
        >
          {t(textKey ?? '')}
        </SSOLink>
      );
    case 'cpanelEmail':
      return (
        <>
          <LoadingButton
            variant={variant}
            onClick={onOpenServerEmail}
            data-testid="email-sso-button"
            disabled={isCpanelEmailDisabled}
            loading={loading}
            {...buttonProps}
            sx={{ textTransform: 'none' }}
          >
            {t(textKey ?? '')}
          </LoadingButton>
        </>
      );
    case 'reseller':
      return (
        <SSOLink
          variant={variant}
          type="reseller"
          queryParams={queryParams}
          hostingId={hostingPlanId}
          data-testid="btn-reseller"
          {...buttonProps}
        >
          {t(textKey ?? '')}
        </SSOLink>
      );
    case 'resellerCpanel':
      return (
        <SSOLink
          variant={variant}
          type="reseller"
          hostingId={hostingPlanId}
          data-testid="btn-reseller-cpanel"
          disabled={!canAccessEmail}
          {...cpanelButtonProps}
        >
          {t(textKey ?? '')}
        </SSOLink>
      );
    case 'manage':
      return (
        <Button
          variant={variant}
          disabled={isSelfManageButtonDisabled}
          onClick={handleManage}
          fullWidth={buttonProps.fullWidth}
          data-testid="vps-manage-app-btn"
        >
          {t(textKey ?? '')}
        </Button>
      );
    default:
      return (
        <SSOLink
          variant={variant}
          type="hosting"
          queryParams={queryParams}
          disabled={isDisabled}
          hostingId={hostingPlanId}
          data-testid={`btn-${(type ?? 'unknown')
            .replace(/([A-Z])/g, '-$1')
            .toLowerCase()}`}
          {...cpanelButtonProps}
        >
          {t(textKey ?? '')}
        </SSOLink>
      );
  }
};

export default ActionButton;
